/**
 * Global HAAT Requests Realtime Notifications Hook
 *
 * Platform-wide FCM listener that shows toast notifications for
 * HAAT Requests events (new requests, status changes) regardless of
 * which sub-app the user is currently viewing.
 *
 * Only activates for users who have HAAT Requests access (appId 12).
 * Toast notifications use a branded realtime variant with a clickable
 * card that navigates to the relevant ticket detail page.
 *
 * Cache invalidation is NOT handled here — that remains in the
 * page-level useHaatRequestsRealtime hook inside haat-requests.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAppPermissions } from './useAppPermissions';
import { notify } from '../shared/services/notification.service';
import { FcmRealtimeEnvelope, subscribeToFcmMessages } from '../realtime/fcm';

const HAAT_REQUESTS_APP_ID = 12;
const HAAT_LOGO_URL =
  'https://cdn.prod.website-files.com/6829acf870093a50cc6ceb69/682b03419ead6e45d8f2c59e_logo%20(2).svg';

interface HaatRealtimeEventPayload {
  encodedId: string;
  publicId: number;
  eventType: 'new_request' | 'status_changed' | 'assigned' | 'unassigned';
  updatedAt: string;
  byUserId: number;
  assignedToUserId?: number | null;
  previousAssignedToUserId?: number | null;
  createdByUserId?: number;
  departmentId?: number;
  subDepartmentId?: number | null;
  fromStatusId?: number;
  toStatusId?: number;
}

export const useGlobalHaatRequestsNotifications = (): void => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hasAccess, isLoading: isPermissionsLoading } = useAppPermissions();
  const navigateRef = useRef(navigate);
  const seenEventKeysRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const hasHaatAccess = !isPermissionsLoading && hasAccess(HAAT_REQUESTS_APP_ID);

  useEffect(() => {
    if (!user?.id || !hasHaatAccess) return;

    const currentUserIdNum = user.id;

    const unsubscribe = subscribeToFcmMessages((envelope: FcmRealtimeEnvelope) => {
      if (envelope.channel !== 'haat-requests') return;

      const payload = envelope.payload as unknown as HaatRealtimeEventPayload;
      if (!payload?.eventType || !payload?.publicId || !payload?.encodedId) return;

      const eventKey = [
        payload.eventType,
        payload.publicId,
        payload.updatedAt || envelope.updatedAt || '',
        payload.byUserId || '',
      ].join(':');

      const now = Date.now();
      seenEventKeysRef.current.forEach((timestamp, key) => {
        if (now - timestamp > 5 * 60 * 1000) {
          seenEventKeysRef.current.delete(key);
        }
      });

      if (seenEventKeysRef.current.has(eventKey)) return;
      seenEventKeysRef.current.set(eventKey, now);

      const actorId = payload.byUserId;
      if (actorId === currentUserIdNum) return;

      const ticketPath = `/haat-requests/requests/${payload.encodedId}`;

      const realtimeOpts = {
        variant: 'realtime' as const,
        appName: 'HCRM',
        appLogoUrl: HAAT_LOGO_URL,
        duration: 0,
        onClickNavigate: () => navigateRef.current(ticketPath),
      };

      if (payload.eventType === 'new_request') {
        notify.custom({
          type: 'info',
          message: `New HCRM request (#${payload.publicId})`,
          ...realtimeOpts,
        });
      } else if (payload.eventType === 'status_changed') {
        notify.custom({
          type: 'info',
          message: `Request #${payload.publicId} status updated`,
          ...realtimeOpts,
        });
      } else if (payload.eventType === 'assigned') {
        const wasReassignedAway =
          Number(payload.previousAssignedToUserId) === currentUserIdNum &&
          Number(payload.assignedToUserId) !== currentUserIdNum;

        if (wasReassignedAway) {
          notify.custom({
            type: 'info',
            message: `You have been unassigned from Request #${payload.publicId}`,
            variant: 'realtime' as const,
            appName: 'HCRM',
            appLogoUrl: HAAT_LOGO_URL,
            duration: 0,
          });
        } else {
          notify.custom({
            type: 'info',
            message: `Request #${payload.publicId} assigned to you`,
            ...realtimeOpts,
          });
        }
      } else if (payload.eventType === 'unassigned') {
        notify.custom({
          type: 'info',
          message: `You have been unassigned from Request #${payload.publicId}`,
          variant: 'realtime' as const,
          appName: 'HCRM',
          appLogoUrl: HAAT_LOGO_URL,
          duration: 0,
        });
      }
    });

    return () => unsubscribe();
  }, [user?.id, hasHaatAccess]);
};
